/**
 * The authentication flows, as thin wrappers over Supabase Auth.
 *
 * Nothing here decides anything. Whether a password is right, whether the account is locked, whether a
 * second factor is required — every one of those is decided by GoTrue and by the database hooks behind it
 * (`iam.password_verification_attempt_hook`, `iam.custom_access_token_hook`). This file turns their answers
 * into the ERP's vocabulary and nothing more.
 *
 * Two facts about the session that the rest of the app relies on:
 *
 *  - **`aal`** (authenticator assurance level) is `aal1` after a password and `aal2` after a verified
 *    second factor. An account with `mfa_required` holds no permission at aal1 — the database refuses with
 *    `RTC_MFA_REQUIRED` — so the shell must not be entered until the level is right.
 *  - **A GoTrue session is not an ERP account.** Someone can sign in and have no `iam.user_accounts` row:
 *    that is exactly the state an invitee is in before they accept. `api.me()` says which.
 */
import { rpc, supabase, toRtcError, type RtcError } from './supabase';

export type Aal = 'aal1' | 'aal2';

export interface AssuranceLevel {
  current: Aal;
  /** What the account needs. `aal2` with `current: aal1` means a challenge is due. */
  next: Aal;
  /** True when a TOTP factor is enrolled and verified. */
  hasVerifiedFactor: boolean;
}

/** GoTrue's refusals, mapped onto the ERP's named errors so the screens render them consistently. */
function toAuthError(error: { message: string; status?: number; code?: string }): RtcError {
  const rtc = toRtcError({ message: error.message, code: error.code });
  // The lockout hook's own words arrive with a 403; anything else about credentials is deliberately
  // vague, and stays that way — saying which half was wrong helps only an attacker.
  if (error.status === 403 && /locked|suspended|no longer active/i.test(error.message)) {
    rtc.code = 'RTC_ACCOUNT_LOCKED';
  } else if (error.status === 400 || error.status === 401) {
    rtc.code = 'RTC_UNAUTHENTICATED';
    rtc.message = 'That email and password combination was not accepted.';
  }
  return rtc;
}

export async function signInWithPassword(email: string, password: string): Promise<void> {
  const { error } = await supabase.auth.signInWithPassword({ email: email.trim(), password });
  if (error) throw toAuthError(error);
}

/** The `session_id` claim of an access token, without trusting it for anything: it only names a storage key. */
function sessionIdOf(accessToken: string): string | null {
  try {
    const payload = accessToken.split('.')[1] ?? '';
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return (JSON.parse(json) as { session_id?: string }).session_id ?? null;
  } catch {
    return null;
  }
}

/**
 * Says "this session exists" to the ERP, once per session per browser tab, so that an administrator can see
 * it and end it. The database reads the session id and the user agent from the request itself; nothing the
 * browser sends here is believed.
 */
export async function announceSession(accessToken: string | undefined): Promise<void> {
  if (!accessToken) return;
  const id = sessionIdOf(accessToken);
  if (!id) return;
  const key = `rtc.session.seen.${id}`;
  try {
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, '1');
  } catch {
    // Storage blocked: announce anyway; the log tolerates repeats.
  }
  try {
    await rpc('record_session_event', { p_event: 'SESSION_SEEN' });
  } catch {
    // No ERP account yet (an invitee), or the account is not active. Nothing to announce.
  }
}

/** Ends every other session of the signed-in account; `includeThisOne` ends this one too. */
export async function signOutEverywhere(includeThisOne: boolean): Promise<void> {
  await rpc('revoke_my_sessions', { p_include_this_one: includeThisOne });
  if (includeThisOne) await supabase.auth.signOut({ scope: 'local' });
}

export async function signOut(): Promise<void> {
  // Recorded before the session is gone, because afterwards there is nobody to record it for. A failure
  // to record must not stop the sign-out: a session that will not end is the worse outcome.
  try {
    await rpc('record_session_event', { p_event: 'SIGN_OUT' });
  } catch {
    // Not signed in as an ERP account (an invitee, or an already-suspended account): nothing to record.
  }
  await supabase.auth.signOut();
}

export async function assuranceLevel(): Promise<AssuranceLevel> {
  const { data, error } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
  if (error) throw toAuthError(error);
  const factors = await supabase.auth.mfa.listFactors();
  return {
    current: (data.currentLevel ?? 'aal1') as Aal,
    next: (data.nextLevel ?? 'aal1') as Aal,
    hasVerifiedFactor: (factors.data?.totp ?? []).some((f) => f.status === 'verified'),
  };
}

export interface Enrolment {
  factorId: string;
  /** An SVG data URI the authenticator app scans. */
  qrCode: string;
  /** For people who cannot scan. */
  secret: string;
}

export async function beginTotpEnrolment(friendlyName = 'RTCOM ERP'): Promise<Enrolment> {
  // An abandoned, unverified factor from a previous attempt blocks a new one with the same name.
  // `totp` lists verified factors only; an abandoned attempt is found under `all`.
  const existing = await supabase.auth.mfa.listFactors();
  for (const factor of existing.data?.all ?? []) {
    if (factor.factor_type === 'totp' && factor.status === 'unverified') {
      await supabase.auth.mfa.unenroll({ factorId: factor.id });
    }
  }

  const { data, error } = await supabase.auth.mfa.enroll({ factorType: 'totp', friendlyName });
  if (error) throw toAuthError(error);
  return { factorId: data.id, qrCode: data.totp.qr_code, secret: data.totp.secret };
}

/** Verifies a six-digit code against a factor and raises the session to aal2. */
export async function verifyTotp(factorId: string, code: string): Promise<void> {
  const { error } = await supabase.auth.mfa.challengeAndVerify({ factorId, code: code.replace(/\s+/g, '') });
  if (error) {
    const rtc = toAuthError(error);
    if (error.status === 422 || /invalid|expired/i.test(error.message)) {
      rtc.code = 'RTC_UNAUTHENTICATED';
      rtc.message = 'That code was not accepted. Codes change every 30 seconds — try the current one.';
    }
    throw rtc;
  }
  // The database cannot see a challenge pass, so the app reports it. Only ever for the session's own
  // account, so the worst a caller can do is add a line to their own history.
  try {
    await rpc('record_session_event', { p_event: 'MFA_PASSED', p_detail: { factor: 'totp' } });
  } catch {
    // An invitee verifying before acceptance has no account yet. The verification itself still stands.
  }
}

/** The first verified TOTP factor, for a challenge. */
export async function verifiedTotpFactorId(): Promise<string | null> {
  const { data } = await supabase.auth.mfa.listFactors();
  return (data?.totp ?? []).find((f) => f.status === 'verified')?.id ?? null;
}

export async function setPassword(password: string): Promise<void> {
  const { error } = await supabase.auth.updateUser({ password });
  if (error) throw toAuthError(error);
}

export async function requestPasswordReset(email: string): Promise<void> {
  // Always resolves the same way whether or not the address exists. Saying otherwise would let anyone
  // enumerate the organisation's accounts by address.
  await supabase.auth.resetPasswordForEmail(email.trim(), { redirectTo: `${window.location.origin}/sign-in` });
}

/** Whether the URL carries a GoTrue invitation or recovery token, which supabase-js consumes on load. */
export function urlCarriesAuthToken(): boolean {
  const hash = window.location.hash;
  const search = window.location.search;
  return /access_token=|type=(invite|recovery|magiclink)/.test(hash) || /[?&]code=/.test(search);
}

/** The password rule the sign-up path enforces; GoTrue enforces its own minimum too. */
export function passwordProblem(password: string): string | null {
  if (password.length < 12) return 'Use at least 12 characters. A longer passphrase beats a complicated short one.';
  if (!/[a-z]/i.test(password) || !/\d/.test(password)) return 'Include at least one letter and one number.';
  return null;
}
